import { useCallback, useEffect, useRef, useState } from 'react';
import { chatApi } from './chatApi';
import { ensureSessionCookie } from './cookieSession';

/**
 * 챗봇 세션·메시지 흐름을 캡슐화하는 hook.
 *
 * 마운트 시 자동 세션 생성. send(text) 호출로 사용자 발화 전송 → 마스킹·분류·응답까지 한 라운드.
 * 응답 후 messages 는 서버 응답(마스킹된 사용자 발화 + assistant) 으로 다시 동기화한다.
 */
export function useChat() {
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [remaining, setRemaining] = useState(null);
  const [nextAction, setNextAction] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  // 담당자(변호사) 타이핑 표시
  const [adminTyping, setAdminTyping] = useState(false);

  const initRef = useRef(false);
  const sendingRef = useRef(false);
  const adminTypingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  const refreshMessages = useCallback(async (sessionId) => {
    try {
      const fresh = await chatApi.getSession(sessionId);
      setMessages(fresh.messages || []);
    } catch (e) {
      // 메시지 재조회 실패는 사용자 흐름을 막지 않는다 — 다음 호출 때 다시 시도됨
      console.warn('chat refresh failed', e);
    }
  }, []);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    ensureSessionCookie();

    (async () => {
      try {
        const data = await chatApi.createSession();
        setSession(data.session);
        setRemaining(data.remaining_free);
        // resumed 세션은 이전 메시지 복원, 신규 세션은 빈 배열 — 양쪽 모두 동일 호출.
        await refreshMessages(data.session.id);
      } catch (e) {
        setError(`세션 생성 실패: ${e.message}`);
      }
    })();
  }, [refreshMessages]);

  // 세션이 정해지면 SSE 구독 — 변호사/봇 새 메시지를 실시간 수신.
  useEffect(() => {
    const sid = session?.id;
    if (!sid) return undefined;
    const es = new EventSource(`/chat/sessions/${sid}/stream`);
    es.onmessage = (e) => {
      let event;
      try {
        event = JSON.parse(e.data);
      } catch {
        return;
      }
      // 담당자 타이핑 신호 — 4초 후 자동 해제
      if (event.type === 'typing' && event.actor === 'admin') {
        setAdminTyping(true);
        clearTimeout(adminTypingTimerRef.current);
        adminTypingTimerRef.current = setTimeout(() => setAdminTyping(false), 4000);
        return;
      }
      // 반대편(어드민/고객)이 상담을 종료 → 세션 상태를 closed 로 갱신
      if (event.type === 'closed') {
        setSession((prev) => (prev ? { ...prev, status: 'closed' } : prev));
        return;
      }
      if (event.type !== 'message') return;
      // 실제 메시지 도착 시 타이핑 표시 즉시 해제
      setAdminTyping(false);
      clearTimeout(adminTypingTimerRef.current);
      setMessages((prev) => {
        if (prev.some((m) => m.id === event.message.id)) return prev;
        // 내 optimistic(임시) 메시지는 정본 도착 시 제거 — send()가 mutex라 최대 1개.
        const cleaned = event.message.role === 'user' ? prev.filter((m) => !m.optimistic) : prev;
        return [...cleaned, event.message];
      });
    };
    // 재연결 시 누락분 보정 — 단 send() 진행 중이면 optimistic 메시지가
    // 서버 목록 교체로 날아가므로 스킵.
    es.onopen = () => {
      if (!sendingRef.current) refreshMessages(sid);
    };
    return () => {
      es.close();
      clearTimeout(adminTypingTimerRef.current);
    };
  }, [session?.id, refreshMessages]);

  // onopen 콜백이 stale closure 없이 최신 sending 값을 읽도록 ref 미러링.
  useEffect(() => {
    sendingRef.current = sending;
  }, [sending]);

  const send = useCallback(
    async (text) => {
      if (!session || sending || !text.trim()) return;
      setSending(true);
      setError(null);

      // 낙관적 사용자 메시지 — 서버 응답 후 마스킹 적용된 정본으로 교체됨
      const optimistic = {
        id: `tmp-${Date.now()}`,
        role: 'user',
        content: text,
        created_at: new Date().toISOString(),
        optimistic: true,
      };
      setMessages((prev) => [...prev, optimistic]);

      try {
        const data = await chatApi.sendMessage(session.id, text);
        setSession(data.session);
        setRemaining(data.remaining_free);
        setNextAction(data.next_action);
      } catch (e) {
        setError(`메시지 전송 실패: ${e.message}`);
        setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      } finally {
        setSending(false);
      }
    },
    [session, sending]
  );

  // 사용자 타이핑 신호 송신 — 2.5초 throttle
  const notifyTyping = useCallback(() => {
    const sid = session?.id;
    if (!sid) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current > 2500) {
      lastTypingSentRef.current = now;
      chatApi.sendTyping(sid, 'user').catch(() => {});
    }
  }, [session?.id]);

  // 새 대화 — 같은 쿠키의 기존 active 세션을 close 하고 새 세션 생성.
  // 메시지는 서버에 그대로 보존됨 (변호사 어드민 조회용). UI 만 비운다.
  const reset = useCallback(async () => {
    if (sending) return;
    setSending(true);
    setError(null);
    try {
      const data = await chatApi.createSession({ fresh: true });
      setSession(data.session);
      setRemaining(data.remaining_free);
      setMessages([]);
      setNextAction(null);
    } catch (e) {
      setError(`새 대화 시작 실패: ${e.message}`);
    } finally {
      setSending(false);
    }
  }, [sending]);

  const submitHotline = useCallback(
    async (phone, summary) => {
      if (!session) return;
      setSending(true);
      setError(null);
      try {
        const data = await chatApi.submitHotline(session.id, phone, summary);
        setSession(data.session);
        setNextAction(data.next_action);
      } catch (e) {
        setError(`핫라인 신청 실패: ${e.message}`);
      } finally {
        setSending(false);
      }
    },
    [session]
  );

  // 상담 종료 — 세션을 closed 로. 종료 후 입력은 막히고 종료 화면이 뜬다.
  const closeConsultation = useCallback(async () => {
    if (!session || sending) return;
    setSending(true);
    setError(null);
    try {
      const data = await chatApi.closeSession(session.id);
      setSession(data.session);
    } catch (e) {
      setError(`상담 종료 실패: ${e.message}`);
    } finally {
      setSending(false);
    }
  }, [session, sending]);

  return {
    session,
    messages,
    remaining,
    nextAction,
    sending,
    error,
    adminTyping,
    closed: session?.status === 'closed',
    send,
    notifyTyping,
    submitHotline,
    closeConsultation,
    reset,
  };
}
